<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useSwipe } from '@vueuse/core';
import ImageLazyload from '@monolith/legacy/components/global/image-lazyload.vue';

const props = withDefaults(
  defineProps<{
    src: string;
    alt: string;
    isOverlayVisible?: boolean;
    /** All product images (primary first). When provided on the simplified tile they power the PRICE-4 gallery. */
    images?: string[];
    /** Enables the hover/swipe gallery. Off for the legacy tile, which keeps the single image + whitening overlay. */
    enableGallery?: boolean;
  }>(),
  {
    isOverlayVisible: false,
    images: () => [],
    enableGallery: false,
  }
);

const emit = defineEmits<{
  /** Emitted on a touch swipe that changes the image, so the tile can track mobile gallery usage (PRICE-4). */
  swiped: [direction: 'next' | 'previous'];
}>();

/** Every image available for the tile, primary first; falls back to the single `src` for legacy tiles. */
const galleryImages = computed(() => {
  const images = props.images.length ? props.images : [props.src];
  return images.filter(Boolean);
});

/** The gallery only kicks in on the simplified tile and when there is more than one image to show. */
const hasGallery = computed(() => props.enableGallery && galleryImages.value.length > 1);

const activeIndex = ref(0);
const activeImage = computed(() => galleryImages.value[activeIndex.value] ?? galleryImages.value[0]);

// Reset to the primary image if the instance is recycled for a different product (e.g. a windowed list).
watch(() => props.images, () => resetImage());

/**
 * Images warmed on first hover so the desktop zone-swap is instant (no network flash). Capped at the
 * three zone targets (images 2-4); mobile swipe lazy-loads the rest on demand as it cycles through.
 */
const isWarmed = ref(false);
const imagesToWarm = computed(() => galleryImages.value.slice(1, 4));

/**
 * Desktop: 3 equal invisible vertical zones map to images 2/3/4. When the product has fewer
 * images, a zone falls back to the last available image rather than showing a blank state.
 */
const showZoneImage = (zoneIndex: number): void => {
  activeIndex.value = Math.min(zoneIndex + 1, galleryImages.value.length - 1);
};

const resetImage = (): void => {
  activeIndex.value = 0;
};

/** Reset to the primary image only when a mouse pointer leaves — a touch swipe must keep its position. */
const onPointerLeave = (event: PointerEvent): void => {
  if (event.pointerType === 'mouse') {
    resetImage();
  }
};

/**
 * Touch/mobile: swipe cycles through the gallery and loops both ways, so retailers never
 * need multiple swipes back to reach the first image. Desktop keeps the hover zones above.
 */
const showNextImage = (): void => {
  activeIndex.value = (activeIndex.value + 1) % galleryImages.value.length;
};

const showPreviousImage = (): void => {
  activeIndex.value = (activeIndex.value - 1 + galleryImages.value.length) % galleryImages.value.length;
};

const imageContainer = ref<HTMLElement | null>(null);

// Touch-event based useSwipe (passive listeners), NOT usePointerSwipe: the latter force-sets
// inline `touch-action: none` on mount, overriding our `pan-y` and blocking vertical page
// scroll that starts on the tile. Text/selection is suppressed via the `--swipeable` class.
useSwipe(imageContainer, {
  onSwipeEnd: (_event, direction) => {
    if (!hasGallery.value) {
      return;
    }
    // Swiping right-to-left (finger moves left) advances; left-to-right goes back.
    if (direction === 'left') {
      showNextImage();
      emit('swiped', 'next');
    } else if (direction === 'right') {
      showPreviousImage();
      emit('swiped', 'previous');
    }
  },
});
</script>

<template>
  <div
    ref="imageContainer"
    class="productImageContainer"
    :class="{ 'productImageContainer--swipeable': hasGallery }"
    data-testid="product-image"
    @pointerenter="isWarmed = true"
    @pointerleave="onPointerLeave"
  >
    <ImageLazyload
      :class="{
        'productImageContainer__image': true,
        'productImageContainer__image--with-overlay': isOverlayVisible && !hasGallery,
      }"
      :src="activeImage"
      :alt="alt"
      :animation-duration="hasGallery ? 0 : undefined"
    />

    <!-- Warm the desktop zone images on first hover so zone-swaps are instant (gallery tiles only). -->
    <img
      v-for="image in isWarmed && hasGallery ? imagesToWarm : []"
      :key="image"
      :src="image"
      alt=""
      aria-hidden="true"
      class="productImageContainer__preload"
    >

    <!-- Desktop: invisible vertical hover zones (image 2/3/4). Touch devices use swipe instead. -->
    <div
      v-if="hasGallery"
      class="productImageContainer__zones"
      aria-hidden="true"
    >
      <div
        v-for="zone in 3"
        :key="zone"
        class="productImageContainer__zone"
        data-testid="product-image-gallery-zone"
        @mouseenter="showZoneImage(zone - 1)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.productImageContainer {
  position: relative;

  // Let the browser keep vertical scrolling while we own horizontal swipes on touch,
  // and suppress selection/callouts during a swipe. Scoped to gallery tiles only.
  &--swipeable {
    touch-action: pan-y;
    user-select: none;
  }

  &__image {
    margin-bottom: theme('spacing.2');
    :deep(img) {
      border: 1px solid theme('colors.neutral.100');
      max-height: 100%;
      border-radius: theme('borderRadius.md');
      position: absolute;
    }

    @media (screen(lg)) {
      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.75);
        transition: opacity 0.2s;
        opacity: 0;
        z-index: 1;
      }
      &--with-overlay::after {
        opacity: 1;
      }
    }
  }

  &__preload {
    display: none;
  }

  // Hover zones are desktop-only, mirroring the tile's `screen(lg)` hover gating. Touch uses swipe.
  &__zones {
    display: none;

    @media (screen(lg)) {
      position: absolute;
      inset: 0;
      z-index: 2;
      display: flex;
    }
  }

  &__zone {
    flex: 1;
    height: 100%;
  }
}
</style>
